import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Edit2, Coins, Crown, Shield, Check, X, Loader2, ShieldAlert, Trash2, LifeBuoy, ShieldCheck
} from "lucide-react";

export default function AdminAccounts() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const nav = useNavigate();

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  // Form states for editing
  const [coins, setCoins] = useState(0);
  const [isVip, setIsVip] = useState(false);
  const [vipUntil, setVipUntil] = useState("");
  const [isPermaVip, setIsPermaVip] = useState(false);
  const [role, setRole] = useState("user");
  const [permissions, setPermissions] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [age, setAge] = useState(0);
  const [gender, setGender] = useState("");
  const [verified, setVerified] = useState(false);
  const [isBrand, setIsBrand] = useState(false);
  const [banned, setBanned] = useState(false);

  const isMaster = currentUser?.is_master || currentUser?.email === "mustafahaidari03@gmail.com";

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const url = search.trim() ? `/admin/users?q=${encodeURIComponent(search.trim())}` : "/admin/users";
      const { data } = await api.get(url);
      setUsers(data || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(fmtError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditClick = (u) => {
    setEditingUser(u);
    setCoins(u.coins || 0);
    const perma = !!u.vip_until?.startsWith("9999");
    setIsPermaVip(perma);
    setIsVip(!!u.vip);
    setVipUntil(perma ? "" : (u.vip_until ? u.vip_until.slice(0, 10) : ""));
    setRole(u.role || "user");
    setPermissions(u.admin_permissions || []);
    setName(u.name || "");
    setEmail(u.email || "");
    setAge(u.age || 0);
    setGender(u.gender || "");
    setVerified(!!u.verified);
    setIsBrand(!!u.is_brand);
    setBanned(!!u.banned);
  };

  const handlePermissionChange = (perm) => {
    if (permissions.includes(perm)) {
      setPermissions(permissions.filter((p) => p !== perm));
    } else {
      setPermissions([...permissions, perm]);
    }
  };

  const saveChanges = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      const payload = {
        coins: Number(coins),
        vip: isVip,
        vip_until: isVip ? (isPermaVip ? "9999-12-31T23:59:59Z" : (vipUntil ? `${vipUntil}T23:59:59Z` : null)) : null,
        name,
        email,
        age: Number(age),
        gender,
        verified,
        is_brand: isBrand,
        banned,
      };

      if (isMaster) {
        payload.role = role;
        payload.admin_permissions = role === "admin" ? permissions : [];
      }

      const { data } = await api.patch(`/admin/users/${editingUser.id}`, payload);
      toast.success("User updated successfully");
      
      // Update local users list
      setUsers(users.map((u) => (u.id === editingUser.id ? data : u)));
      setEditingUser(null);
    } catch (e) {
      toast.error(fmtError(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async () => {
    if (!editingUser) return;
    if (!window.confirm("Are you absolutely sure you want to permanently delete this member's account? This will cascade-delete their activities, posts, friends list, and clean up their entire profile. This action is irreversible.")) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${editingUser.id}`);
      toast.success("User account deleted successfully");
      setUsers(users.filter((u) => u.id !== editingUser.id));
      setEditingUser(null);
    } catch (e) {
      toast.error(fmtError(e));
    } finally {
      setDeleting(false);
    }
  };

  if (forbidden || (currentUser && currentUser.role !== "admin" && !isMaster)) {
    return (
      <div className="max-w-md mx-auto pt-24 px-6 text-center">
        <div className="card-nb bg-white p-8">
          <ShieldAlert className="w-10 h-10 text-[#FF7A6B] mx-auto" strokeWidth={2.5} />
          <h2 className="mt-4 font-display font-black text-2xl">Forbidden</h2>
          <p className="mt-2 text-[#3D424B]">Only administrators can access this section.</p>
          <button onClick={() => nav("/feed")} className="btn-nb btn-nb-primary mt-6">
            {t("common.back")}
          </button>
        </div>
      </div>
    );
  }

  const ADMIN_PERMISSIONS_LIST = [
    { key: "tickets", label: "Support Tickets", desc: "Read and reply to support/help tickets" },
    { key: "reports", label: "Reports Moderation", desc: "View and resolve/dismiss user reports" },
    { key: "users", label: "User Management", desc: "List, edit coins, VIP status, and roles" },
    { key: "metrics", label: "Economy Metrics", desc: "View transaction and business metrics" },
    { key: "cashouts", label: "Cashouts Queue", desc: "View and approve bank cashout requests" },
    { key: "verifications", label: "ID Verifications", desc: "Review, approve, or reject ID submissions" }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 lg:px-8 lg:py-10 pb-28">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/admin")} className="btn-nb p-2.5 sm:p-3">
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <div>
            <div className="font-script text-lg sm:text-2xl text-[#14C8B0]">Dashboard</div>
            <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight">Members Management</h1>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 w-full sm:w-80">
          <Link to="/admin/tickets" className="btn-nb !px-3 !py-2 text-xs sm:text-sm font-bold justify-center text-center whitespace-nowrap">
            <LifeBuoy className="w-4 h-4 me-1.5 inline" /> Support Tickets
          </Link>
          <Link to="/admin/verifications" className="btn-nb btn-nb-dark !px-3 !py-2 text-xs sm:text-sm font-bold justify-center text-center whitespace-nowrap">
            <ShieldCheck className="w-4 h-4 me-1.5 inline" /> ID Reviews
          </Link>
        </div>
      </div>

      {/* Search Input */}
      <div className="relative mb-6">
        <span className="absolute inset-y-0 start-0 flex items-center ps-4 pointer-events-none">
          <Search className="w-5 h-5 text-[#7C828D]" />
        </span>
        <input
          type="text"
          className="input-nb ps-11 w-full text-base py-3"
          placeholder="Search by name, email, or user ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* User Table Card */}
      <div className="card-nb bg-white overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-[#7C828D]">No accounts found matching search criteria.</div>
        ) : (
          <table className="w-full text-start border-collapse">
            <thead>
              <tr className="border-b border-[#EFEEE6]">
                <th className="p-4 text-start font-display font-bold text-xs uppercase tracking-wider text-[#7C828D]">{t("auth.yourName")}</th>
                <th className="p-4 text-start font-display font-bold text-xs uppercase tracking-wider text-[#7C828D]">{t("auth.email")}</th>
                <th className="p-4 text-center font-display font-bold text-xs uppercase tracking-wider text-[#7C828D]">Role</th>
                <th className="p-4 text-center font-display font-bold text-xs uppercase tracking-wider text-[#7C828D]">T-Coins</th>
                <th className="p-4 text-center font-display font-bold text-xs uppercase tracking-wider text-[#7C828D]">VIP Status</th>
                <th className="p-4 text-end font-display font-bold text-xs uppercase tracking-wider text-[#7C828D]">Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isUserVip = !!u.vip;
                return (
                  <tr key={u.id} className="border-b border-[#EFEEE6] last:border-0 hover:bg-[#FFFDF7]/50 transition-colors">
                    <td className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full border-2 border-ink bg-[#FFF1D6] flex items-center justify-center font-display font-black overflow-hidden shadow-nb-sm">
                        {u.avatar_url ? (
                          <img src={u.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          u.name?.[0]?.toUpperCase()
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-extrabold truncate flex items-center gap-1">
                          {u.name}
                          {u.verified && <Check className="w-4 h-4 text-[#14C8B0]" strokeWidth={3} />}
                        </div>
                        <div className="text-[10px] font-mono text-[#7C828D] truncate">{u.id}</div>
                      </div>
                    </td>
                    <td className="p-4 font-medium text-sm text-[#3D424B]">{u.email}</td>
                    <td className="p-4 text-center">
                      <span className={`px-2 py-0.5 rounded-full border border-ink text-xs font-display font-bold ${
                        u.role === "admin" ? "bg-[#FF7A6B] text-white" : "bg-[#EFEEE6]"
                      }`}>
                        {u.is_master ? "Master Admin" : u.role}
                      </span>
                    </td>
                    <td className="p-4 text-center font-mono font-bold text-sm">
                      <div className="flex items-center justify-center gap-1">
                        <Coins className="w-4 h-4 text-[#F9C349]" />
                        {u.coins || 0}
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      {isUserVip ? (
                        <div className="flex flex-col items-center">
                          <span className="tag-nb bg-[#BFE9D6] text-[#0A5C36] border-transparent text-[10px] flex items-center gap-1 font-bold">
                            <Crown className="w-3 h-3" /> VIP Active
                          </span>
                          {u.vip_until && (
                            <span className="text-[9px] text-[#7C828D] mt-0.5 font-mono">
                              Until {u.vip_until.slice(0, 10)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-[#7C828D]">—</span>
                      )}
                    </td>
                    <td className="p-4 text-end">
                      <button
                        onClick={() => handleEditClick(u)}
                        className="btn-nb p-2 inline-flex items-center justify-center"
                        disabled={u.is_master && !isMaster}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border-2 border-ink rounded-2xl shadow-nb max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative">
            <button
              onClick={() => setEditingUser(null)}
              className="absolute top-4 end-4 w-8 h-8 rounded-full border-2 border-ink shadow-nb-sm flex items-center justify-center hover:-translate-y-0.5 transition-transform bg-white"
            >
              <X className="w-4 h-4" strokeWidth={2.5} />
            </button>

            <h2 className="font-display font-black text-2xl mb-4">Edit User Account</h2>
            <div className="flex items-center gap-3 mb-6 p-3 bg-[#FFFDF7] rounded-xl border border-ink/10">
              <div className="w-12 h-12 rounded-full border-2 border-ink bg-[#FFF1D6] flex items-center justify-center font-display font-black overflow-hidden">
                {editingUser.avatar_url ? (
                  <img src={editingUser.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  editingUser.name?.[0]?.toUpperCase()
                )}
              </div>
              <div>
                <div className="font-display font-extrabold text-lg">{editingUser.name}</div>
                <div className="text-xs text-[#7C828D]">{editingUser.email}</div>
              </div>
            </div>

            <div className="space-y-4">
              {/* T-Coins Section */}
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">T-Coins</label>
                <div className="relative">
                  <span className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                    <Coins className="w-5 h-5 text-[#F9C349]" />
                  </span>
                  <input
                    type="number"
                    min={0}
                    className="input-nb ps-10 w-full"
                    value={coins}
                    onChange={(e) => setCoins(e.target.value)}
                  />
                </div>
              </div>

              {/* Profile Details Edit */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">Name</label>
                  <input
                    type="text"
                    className="input-nb w-full"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">Email</label>
                  <input
                    type="email"
                    className="input-nb w-full"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">Age</label>
                  <input
                    type="number"
                    min={0}
                    className="input-nb w-full"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">Gender</label>
                  <select
                    className="input-nb w-full"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    <option value="">None</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-binary</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Toggles */}
              <div className="flex flex-wrap gap-4 p-3 bg-[#F4F2ED] rounded-xl border border-ink/10">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-ink rounded"
                    checked={verified}
                    onChange={(e) => setVerified(e.target.checked)}
                  />
                  <span className="text-xs font-display font-bold">Verified Badge</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-ink rounded"
                    checked={isBrand}
                    onChange={(e) => setIsBrand(e.target.checked)}
                  />
                  <span className="text-xs font-display font-bold">Is Brand</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-ink rounded"
                    checked={banned}
                    onChange={(e) => setBanned(e.target.checked)}
                  />
                  <span className="text-xs font-display font-bold text-red-600">Banned</span>
                </label>
              </div>

              {/* VIP Membership Section */}
              <div className="p-4 bg-[#F4F2ED] rounded-xl border border-ink/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-[#FFB347]" />
                    <div>
                      <div className="font-display font-bold text-sm">VIP Membership</div>
                      <div className="text-xs text-[#7C828D]">Enable premium features</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="w-5 h-5 accent-ink rounded"
                    checked={isVip}
                    onChange={(e) => setIsVip(e.target.checked)}
                  />
                </div>

                {isVip && (
                  <div className="mt-4 pt-3 border-t border-ink/10 space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-ink rounded"
                        checked={isPermaVip}
                        onChange={(e) => {
                          setIsPermaVip(e.target.checked);
                          if (e.target.checked) setVipUntil("");
                        }}
                      />
                      <span className="text-xs font-display font-bold">Permanent VIP (No Expiry)</span>
                    </label>

                    {!isPermaVip && (
                      <div>
                        <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1 text-[#7C828D]">Expires On</label>
                        <input
                          type="date"
                          className="input-nb w-full bg-white"
                          value={vipUntil}
                          onChange={(e) => setVipUntil(e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Roles & Permissions (Master Only) */}
              {isMaster && !editingUser.is_master && (
                <div className="p-4 bg-[#FFFDF7] rounded-xl border-2 border-ink/20 space-y-4">
                  <div>
                    <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">Account Role</label>
                    <select
                      className="input-nb w-full"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                    >
                      <option value="user">User</option>
                      <option value="admin">Administrator</option>
                    </select>
                  </div>

                  {role === "admin" && (
                    <div className="pt-2 border-t border-ink/10">
                      <label className="block text-xs font-display font-bold uppercase tracking-widest mb-3">Admin Permissions</label>
                      <div className="space-y-2">
                        {ADMIN_PERMISSIONS_LIST.map((p) => {
                          const checked = permissions.includes(p.key);
                          return (
                            <label key={p.key} className="flex items-start gap-3 p-2 rounded-lg hover:bg-black/5 cursor-pointer transition-colors">
                              <input
                                type="checkbox"
                                className="w-4 h-4 accent-ink mt-0.5 rounded"
                                checked={checked}
                                onChange={() => handlePermissionChange(p.key)}
                              />
                              <div>
                                <div className="font-display font-bold text-xs">{p.label}</div>
                                <div className="text-[10px] text-[#7C828D]">{p.desc}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Danger Zone */}
              {!editingUser.is_master && editingUser.email !== "mustafahaidari03@gmail.com" && (
                <div className="mt-6 p-4 rounded-xl border-2 border-[#FF7A6B]/20 bg-[#FFE6E0]/20 space-y-2">
                  <h3 className="font-display font-black text-sm text-[#D14848]">Danger Zone</h3>
                  <p className="text-[10px] text-[#7C828D]">Permanently delete this member's account and all associated posts, activities, and friends. This action cannot be undone.</p>
                  <button
                    onClick={deleteAccount}
                    className="btn-nb btn-nb-coral w-full py-2 text-xs flex items-center justify-center gap-1 font-bold"
                    disabled={deleting}
                  >
                    {deleting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <>
                        <Trash2 className="w-3.5 h-3.5" /> Delete Account
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6 pt-4 border-t border-[#EFEEE6]">
              <button
                onClick={() => setEditingUser(null)}
                className="btn-nb flex-1"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={saveChanges}
                className="btn-nb btn-nb-primary flex-1 flex items-center justify-center gap-2"
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4" /> Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
